import usePaginate from "@/components/paginate/usePaginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import { Card, Stack, Typography } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import { CommonCostChart } from "@/pages/query/common/charts/CommonCostChart";
import { CommonJobListTable } from "@/pages/query/common/tables/CommonJobListTable";
import { useEffect, useState } from "react";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import {
  getStoredProceduresJobs,
  getStoredProceduresJobsGraphData,
  getStoredProceduresJobsGraphSummary,
  getStoredProceduresJobsListFilterValues,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { Spinner } from "@altimateai/lego";
import { FilterApplicableFor } from "@/components/tags/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const StoredProceduresRoot = () => {
  useBreadcrumb("Stored Procedures", {
    defaultParents: [{ label: "Code", pathname: "/query" }],
    truncateTo: "/query",
  });
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { searchParams } = useFiltersBySearchParams();
  const sortAttribute = searchParams.get("sortAttribute") ?? "cost";
  const sortOrder = searchParams.get("sortOrder") ?? "desc";
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sync URL params with filters state on mount and when URL changes
  useEffect(() => {
    const urlFilters: Record<string, string | string[]> = {};
    const warehouses = searchParams.getAll("warehouses");
    if (warehouses.length > 0) {
      urlFilters.warehouses = warehouses;
    }
    const databases = searchParams.getAll("databases");
    if (databases.length > 0) {
      urlFilters.databases = databases;
    }
    setFilters(urlFilters);
  }, [searchParams]);

  const handleFilterChange = (key: string, value: string | string[]) => {
    setPage(0); // Reset to first page on filter change
    setFilters({
      ...filters,
      [key]: value,
    });
  };

  const getFilterParams = () => {
    const params = {
      page: page + 1,
      size: pageSize,
      sort: sortAttribute,
      sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      ...filters,
    };
    return params;
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "stored_procedures_jobs",
      page,
      pageSize,
      startDate,
      endDate,
      sortAttribute,
      sortOrder,
      filters,
    ],
    queryFn: () => getStoredProceduresJobs(getFilterParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.pages ?? 0);
    }
  }, [data]);

  const dateParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
  };

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["stored_procedures_graph_data", startDate, endDate],
    queryFn: () => getStoredProceduresJobsGraphData(dateParams),
  });

  const graphSummaryQueryKey = ["stored_procedures_graph_summary", startDate, endDate];

  const { data: apiFilters } = useQuery({
    queryKey: ["stored_procedures_job_list_filters", startDate, endDate],
    queryFn: () => getStoredProceduresJobsListFilterValues(dateParams),
  });

  return (
    <Card className="al-p-4">
      <Stack direction="column">
        <Stack align={"center"} justify={"between"}>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Typography variant="page-heading">Stored Procedures</Typography>
            <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.WORKLOADS} />
          </Stack>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
        <Stack direction="column">
          {isGraphLoading ? (
            <Stack
              align={"center"}
              justify={"center"}
              className={"al-w-full al-border al-rounded-md al-min-h-32"}
            >
              <Spinner />
              Loading graph...
            </Stack>
          ) : graphData?.data && graphData.data.length > 0 ? (
            <CommonCostChart
              graphData={graphData.data ?? []}
              graphSummaryQueryKey={graphSummaryQueryKey}
              graphSummaryQueryFn={getStoredProceduresJobsGraphSummary}
              graphSummaryParams={dateParams}
            />
          ) : (
            <div className="al-w-full al-text-center">No data is present</div>
          )}
        </Stack>
        <Stack direction={"column"} gap={"lg"} className={"al-mt-4"}>
          <Typography variant="page-heading">Job list</Typography>
          <CommonJobListTable
            isLoading={isLoading}
            basePath={"stored-procedures"}
            tableData={data}
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            setPage={setPage}
            updatePageSize={updatePageSize}
            handleFilterChange={handleFilterChange}
            filters={{
              warehouses: apiFilters?.warehouses ?? [],
              databases: apiFilters?.databases ?? [],
            }}
            showQueryTextOnHover
            showDatabaseColumn
            applicableFor={FilterApplicableFor.STORED_PROCEDURES}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
